<template>
  <div class="feed">
    <Recommendations />

    <FeedTypePanel v-if="posts.length" v-model:viewType="viewType" />

    <component
      v-for="post in posts"
      :key="post.id"
      :is="currentPostComponent"
      :post="post"
      @open="openPost"
    />

    <div v-if="loadPostsError" class="error-message">
      {{ $t("feed.error") }}
    </div>

    <div
      v-show="auth.user && posts.length === 0 && !isLoading && !loadPostsError"
      class="error-message"
    >
      <img src="/LogoIcon.svg" />
      <p>{{ $t("feed.feedEmpty") }}</p>
    </div>

    <div ref="observer" class="observer"></div>

    <PostShortLoader v-if="isLoading && viewType === 'short'" />
    <PostLoader v-else-if="isLoading && viewType === 'default'" />
  </div>
</template>

<script>
import PostLoader from "@/UI/Loaders/PostLoader.vue";
import Recommendations from "./Recommendations.vue";
import { getPostsPage, getFeedPage } from "../../api/axios.js";
import { useAuthStore } from "../../stores/authStore.js";
import { useOpenPost } from "../../composables/useOpenPost.js";
import { useThemeStore } from "@/stores/themeStore";
import { FEED_VIEW_TYPES } from "@/constants/feedViewTypes";
import { FEED_VIEW_COMPONENTS } from "./feedViewMap.js";
import PostShortLoader from "@/UI/Loaders/PostShortLoader.vue";
import FeedTypePanel from "./FeedTypePanel.vue";

export default {
  name: "Feed",
  components: { PostLoader, Recommendations, PostShortLoader, FeedTypePanel },
  data() {
    return {
      isLoading: false,
      posts: [],
      page: 1,
      hasMore: true,
      loadPostsError: false,
      auth: useAuthStore(),
      openPost: useOpenPost().openPost,
      themeStore: useThemeStore(),
      viewType:
        localStorage.getItem("feed_view_type") || FEED_VIEW_TYPES.DEFAULT,
    };
  },
  computed: {
    currentPostComponent() {
      return FEED_VIEW_COMPONENTS[this.viewType];
    },
  },
  methods: {
    async loadPosts() {
      if (this.isLoading || !this.hasMore) return;

      this.isLoading = true;
      try {
        const res = this.auth.token
          ? await getFeedPage(this.page)
          : await getPostsPage(this.page);

        this.savePosts(res);
      } catch (err) {
        console.error("Error loading posts: ", err);
        this.loadPostsError = true;
      } finally {
        this.isLoading = false;
      }
    },
    savePosts(response) {
      const newPosts = response.data.results;
      this.posts = [...this.posts, ...newPosts];
      this.page += 1;
      this.hasMore = !!response.data.next;
    },
    resetFeed() {
      this.posts = [];
      this.page = 1;
      this.hasMore = true;
      this.loadPosts();
    },
  },
  mounted() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading) {
          this.loadPosts();
        }
      },
      {
        rootMargin: "50px",
        threshold: 1.0,
      },
    );

    const route = this.$route;
    if (route.name === "Post" && route.params.id) {
      this.openPost(route.params.id);
    }

    observer.observe(this.$refs.observer);
  },
  watch: {
    "auth.token"() {
      this.resetFeed();
    },

    viewType(newVal) {
      localStorage.setItem("feed_view_type", newVal);
    },
  },
};
</script>

<style>
.feed {
  width: min(100%, 657px);
  margin: 0 auto;
  position: relative;
}

.error-message {
  margin-top: 100px;
  color: #454545;
  text-align: center;
  padding: 10px 100px;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  justify-content: center;
}

.error-message img {
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
}

.error-message p {
  font-size: 16px;
  color: #5a5a5a;
}

@media (max-width: 500px) {
  .post {
    margin-bottom: 12px;
  }
}
</style>
